/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState, ReactElement } from "react";

import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";

import { PrimaryButton, Stack } from "@fluentui/react";

import ListCounts from "Components/Common/ListCounts/ListCounts";
import ManageCodesList from "Components/ManageCodesList/ManageCodesList";
import { manageCodesFilterFields } from "Constants/ManageCodesConstants";
import {
  buttonStackTokens,
  containerStackStyles,
  filterButtonStyles,
  listStackStyles,
} from "Containers/Common/CommonContainer.styles";
import SideBarContainer from "Containers/SideBarContainer/SideBarContainer";
import {
  getBenefitNamesOptionList,
  getCurrentPage,
  getFilteredCount,
  getPageSize,
  getSelectedBenefitName,
  setBenefitNamesOptionList,
  setCurrentPage,
  setPageSize,
  setSelectedBenefitName,
} from "Dataflows/Benefit/ManageCodesSlice";
import { IFilterValue } from "Interfaces/IFilterValue";
import { IKeyText } from "Interfaces/IKeyText";
import { useGetBenefitNamesQuery } from "Services/ManageCodesApi";

/**
 * The Azure Benefit Requests Container.
 */
const ManageCodesContainer: React.FunctionComponent = (): ReactElement => {
  const dispatch = useDispatch();
  const { tenant } = useParams<{ tenant: string }>();
  const [appliedBenefitName, setAppliedBenefitName] = useState<string>("");

  const {
    data: benefitNames,
    isLoading: isBenefitNamesLoading,
    error: benefitNamesApiError,
  } = useGetBenefitNamesQuery(tenant);

  useEffect(() => {
    let benefitNamesOptionList: IKeyText[] = [];
    benefitNamesOptionList.push({ key: "Any", text: "Any" });
    if (benefitNamesApiError && !benefitNames) {
      benefitNamesOptionList = [{ key: "Any", text: "Any" }];
    } else if (Array.isArray(benefitNames)) {
      benefitNamesOptionList = [
        { key: "Any", text: "Any" }, // Adding "Any" as always the first option
        ...benefitNames.map((name) => ({
          key: name,
          text: name,
        })),
      ];
    } else if (typeof benefitNames === "string") {
      benefitNamesOptionList = [
        { key: "Any", text: "Any" }, // Adding "Any" as  always the first option
        ...benefitNames.split(",").map((name) => ({
          key: name.trim(),
          text: name.trim(),
        })),
      ];
    }

    manageCodesFilterFields[0].options = benefitNamesOptionList;
    dispatch(setBenefitNamesOptionList(benefitNamesOptionList));
  }, [benefitNames]);

  const benefitName = useSelector(getSelectedBenefitName);
  const benefitNamesOptionList = useSelector(getBenefitNamesOptionList);
  const currentPage = useSelector(getCurrentPage);
  const filteredCount = useSelector(getFilteredCount);
  const pageSize = useSelector(getPageSize);

  const selectedFilterValues: IFilterValue[] = benefitName
    ? [
        {
          fieldName: "benefitName", // This must match the field name in manageCodesFilterFields
          value: benefitName,
        },
      ]
    : [];

  const addFilterValue = (filterValue: IFilterValue) => {
    return dispatch(setSelectedBenefitName(filterValue.value));
  };

  const removeFilterValue = () => {
    return dispatch(setSelectedBenefitName("Any"));
  };

  const updateFilterValue = (filterValue: IFilterValue): void => {
    dispatch(setSelectedBenefitName(filterValue.value));
  };

  function handleApplyFilter(currentPage: number, pageSize: number): void {
    benefitName === "Any" ? setAppliedBenefitName("") : setAppliedBenefitName(benefitName);
    dispatch(setCurrentPage(currentPage));
    dispatch(setPageSize(pageSize));
  }

  return (
    <Stack horizontal styles={containerStackStyles}>
      <Stack.Item disableShrink>
        <SideBarContainer
          benefitNamesOptionList={benefitNamesOptionList}
          benefitNamesLoadError={
            benefitNamesApiError && !benefitNames && "data" in benefitNamesApiError
              ? (benefitNamesApiError.data as any)?.error?.message ||
                "An unexpected error occurred."
              : ""
          }
          selectedFilterValues={selectedFilterValues}
          filterFields={manageCodesFilterFields}
          addFilterValue={addFilterValue}
          removeFilterValue={removeFilterValue}
          updateFilterValue={updateFilterValue}
        />
        <Stack horizontal tokens={buttonStackTokens}>
          <PrimaryButton
            styles={filterButtonStyles()}
            text="Apply"
            onClick={(): void => handleApplyFilter(currentPage, pageSize)}
          />
          <PrimaryButton
            styles={filterButtonStyles()}
            text="Reset"
            onClick={() => {
              dispatch(setSelectedBenefitName("Any"));
              setAppliedBenefitName("");
            }}
          />
        </Stack>
      </Stack.Item>
      <Stack.Item grow styles={listStackStyles()}>
        <ManageCodesList
          currentPage={currentPage}
          pageSize={pageSize}
          filteredCount={filteredCount}
          tenant={tenant}
          benefitName={appliedBenefitName}
        />
      </Stack.Item>
    </Stack>
  );
};

export default ManageCodesContainer;
